import { scopedSetter } from "../api/scopedSetter.js";
import { create } from "zustand";
import { toast } from "../ui/Toast.js";
import { t } from "../i18n/index.js";
import { errorText } from "../base/state.js";
import { achievementsApi, dailyApi, eventsApi, growthApi } from "./api.js";
import type { AchievementsView, CalendarEntry, ClaimableList, DailyView, EventModifier, EventRunView, GrowthView, LootView } from "./types.js";

/** Five tabs: Live · Daily · Progress (growth + achievements) · Battle Pass · Calendar (D68). */
export type EventsTab = "plaza" | "daily" | "progress" | "pass" | "calendar";
export const EVENTS_TABS: EventsTab[] = ["plaza", "daily", "progress", "pass", "calendar"];

interface EventsState {
  runs: EventRunView[] | null;
  modifiers: EventModifier[];
  daily: DailyView | null;
  growth: GrowthView | null;
  achievements: AchievementsView | null;
  calendar: CalendarEntry[] | null;
  /** Everything collectable right now; drives the rewards strip and the hub badge (D68). */
  claimable: ClaimableList | null;
  /** Claimables and new-day pushes since the last visit (HUD badge). */
  attention: number;
  refreshRuns(): Promise<void>;
  refreshDaily(): Promise<void>;
  refreshGrowth(): Promise<void>;
  refreshAchievements(): Promise<void>;
  refreshCalendar(days?: number): Promise<void>;
  refreshClaimable(): Promise<void>;
  claimAll(): Promise<{ claimed: number; skipped: number; loot: LootView } | null>;
  bump(): void;
  seen(): void;
  run<T>(op: () => Promise<T>, okText?: string): Promise<T | null>;
}

export const useEvents = create<EventsState>((set, get) => ({
  runs: null,
  modifiers: [],
  daily: null,
  growth: null,
  achievements: null,
  calendar: null,
  claimable: null,
  attention: 0,
  refreshRuns: async () => {
    const set = scopedSetter<EventsState>(useEvents.setState);
    try {
      const res = await eventsApi.list();
      set({ runs: res.runs, modifiers: res.modifiers });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshDaily: async () => {
    const set = scopedSetter<EventsState>(useEvents.setState);
    try {
      set({ daily: await dailyApi.view() });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshGrowth: async () => {
    const set = scopedSetter<EventsState>(useEvents.setState);
    try {
      set({ growth: await growthApi.view() });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshAchievements: async () => {
    const set = scopedSetter<EventsState>(useEvents.setState);
    try {
      set({ achievements: await achievementsApi.list() });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshCalendar: async (days = 14) => {
    const set = scopedSetter<EventsState>(useEvents.setState);
    try {
      set({ calendar: (await eventsApi.calendar(days)).runs });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshClaimable: async () => {
    const set = scopedSetter<EventsState>(useEvents.setState);
    try {
      set({ claimable: await eventsApi.claimable() });
    } catch {
      /* logged out */
    }
  },
  claimAll: async () => {
    const res = await get().run(() => eventsApi.claimAll());
    if (res) {
      if (res.claimed > 0) toast.success(t("events.rewards.claimed", { n: res.claimed }));
      void Promise.all([get().refreshClaimable(), get().refreshRuns(), get().refreshDaily(), get().refreshGrowth(), get().refreshAchievements()]);
    }
    return res;
  },
  bump: () => set((s) => ({ attention: s.attention + 1 })),
  seen: () => set({ attention: 0 }),
  run: async (op, okText) => {
    const set = scopedSetter<EventsState>(useEvents.setState);
    try {
      const res = await op();
      if (!set.current()) return null;
      if (okText) toast.success(okText);
      return res;
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
      return null;
    }
  },
}));

/** Claimable things across the daily loop for badges: login, chests, milestones. */
export function claimableCount(s: Pick<EventsState, "daily" | "runs" | "growth">): number {
  let n = 0;
  if (s.daily?.login.claimable) n++;
  if (s.daily?.login.day28.claimable) n++;
  n += s.daily?.activity.chests.filter((c) => c.claimable).length ?? 0;
  n += s.daily?.intel.missions.filter((m) => m.claimable).length ?? 0;
  for (const r of s.runs ?? []) n += r.milestones.filter((m) => m.claimable).length;
  n += s.growth?.missions.filter((m) => m.done && !m.claimed).length ?? 0;
  return n;
}
